import type { SpinCell } from "./board.ts";
import type { DiceParams } from "./params.ts";
import { effectiveActualTotal, formulaTotal, levelRuntimeB, rivalTotal, type Globals, type Level } from "./model.ts";
import { eMilliOf } from "./curve.ts";
import { divRound, toBig } from "./big.ts";
import { costSourceKind, costSourceRival } from "./costSource.ts";

export interface PaceConfig {
  spins: SpinCell[];
  dice: Pick<DiceParams, "cap" | "capStep" | "capMax" | "regen">;
  nodes: Array<{ seq: number; line: string; value: number; unlockLevel: number }>;
  costSource: string;
}
export interface PaceScenario {
  visitsPerDay: number;
  entryDice: number;
  extraDicePerDay: number;
  claimTalents: boolean;
}
export const DEFAULT_PACE_SCENARIO: PaceScenario = { visitsPerDay: 3, entryDice: 0, extraDicePerDay: 0, claimTalents: true };

/** Direct dice/free-ticket returns only. Missing or self-sustaining tables have no finite estimate. */
export function spinReturnFraction(spins: SpinCell[]): { numerator: bigint; denominator: bigint } | null {
  let weighted = 0n, weight = 0n;
  for (const cell of spins) {
    if (!cell.enabled || cell.weight === 0) continue;
    if (!Number.isSafeInteger(cell.weight) || cell.weight < 0) return null;
    const w = BigInt(cell.weight);
    weight += w;
    for (const reward of cell.rewards) {
      if (reward.kind !== "dice" && reward.kind !== "free") continue;
      const range = reward.kind === "dice" && reward.mode === "range";
      const min = range ? reward.min : reward.value, max = range ? reward.max : reward.value;
      if (min == null || max == null || !Number.isSafeInteger(min) || !Number.isSafeInteger(max) || min < 0 || max < min) return null;
      weighted += w * (BigInt(min) + BigInt(max));
    }
  }
  if (weight <= 0n || weighted >= weight * 2n) return null;
  return { numerator: weighted, denominator: weight * 2n };
}

const whole = (value: number | null): value is number => value != null && Number.isSafeInteger(value) && value >= 0;
const ceilDiv = (n: bigint, d: bigint) => (n + d - 1n) / d;
export const validPaceScenario = (scenario: PaceScenario) => whole(scenario.entryDice) && whole(scenario.extraDicePerDay)
  && Number.isSafeInteger(scenario.visitsPerDay) && scenario.visitsPerDay >= 1 && scenario.visitsPerDay <= 24;

/** A budget estimate, not a player simulation: no claims, carried coins, losses or purchases. */
export function progressionPace(levels: Level[], globals: Globals, config: PaceConfig, scenario: PaceScenario) {
  const returns = spinReturnFraction(config.spins);
  const kind = costSourceKind(config.costSource);
  const rival = costSourceRival(config.costSource);
  const source = kind === "formula" ? "formula" : kind === "manual" ? "manual" : config.costSource;
  const legacyB = globals.economyModel === 2 ? [] : levelRuntimeB(levels, globals, source);
  const nodes = [...config.nodes].sort((a, b) => a.seq - b.seq);
  const validScenario = validPaceScenario(scenario);
  const unlockedByLevel = new Map<number, number>();
  let buildings = 0;
  for (const level of [...levels].sort((a, b) => a.id - b.id)) {
    unlockedByLevel.set(level.id, buildings);
    buildings += level.N;
  }
  return levels.map((level, index) => {
    const cost = kind === "formula" ? formulaTotal(level, globals) : kind === "manual" ? effectiveActualTotal(level, globals)
      : rival ? rivalTotal(level, rival) : null;
    const g = globals.economyModel === 2 ? toBig(level.gMilli ?? null)
      : legacyB[index] != null && Number.isFinite(globals.E) && globals.E > 0 ? legacyB[index]! * eMilliOf(globals.E) : null;
    const netMilli = cost != null && cost >= 0n && g != null && g > 0n && returns
      ? divRound(cost * 1_000_000n * (returns.denominator - returns.numerator), g * returns.denominator) : null;
    let capBonus = 0, regenSec = config.dice.regen != null && config.dice.regen >= 0 ? Math.round(config.dice.regen * 60) : null;
    const unlocked = unlockedByLevel.get(level.id)!;
    if (scenario.claimTalents && Number.isSafeInteger(unlocked)) for (const node of nodes) {
      if (node.unlockLevel > unlocked) continue;
      if (node.line === "stamina_cap") capBonus = node.value;
      if (node.line === "dice_regen") regenSec = node.value;
    }
    const { cap, capStep, capMax } = config.dice;
    const capacity = (!scenario.claimTalents || Number.isSafeInteger(unlocked)) && whole(cap) && whole(capStep) && whole(capMax) && whole(capBonus)
      ? Math.min(capMax, cap + capStep * (level.id - 1) + capBonus) : null;
    const naturalPerVisit = validScenario && capacity != null && whole(regenSec)
      ? regenSec === 0 ? 0 : Math.min(capacity, Math.floor(86400 / (regenSec * scenario.visitsPerDay))) : null;
    const missing = netMilli != null && validScenario ? netMilli - BigInt(scenario.entryDice) * 1000n : null;
    const perDay = naturalPerVisit != null && validScenario ? naturalPerVisit * scenario.visitsPerDay + scenario.extraDicePerDay : null;
    const visits = missing == null ? null : missing <= 0n ? 0n : perDay != null && Number.isSafeInteger(perDay) && perDay > 0
      ? ceilDiv(missing * BigInt(scenario.visitsPerDay), BigInt(perDay) * 1000n) : null;
    return { level: level.id, netMilli, visits, capacity, regenSec, naturalPerVisit };
  });
}
